import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button, confirmDialog } from "@/components/ui";
import { BackgroundAssetServices } from "@/services/bedrock/BackgroundAssetServices";
import { BackgroundAsset } from "@/services/bedrock/dtos/BackgroundAsset";
import { designKeys } from "@/services/bedrock/queryKeys";
import { AssetSelection } from "./AssetGrid";
import { LibraryMode, LibraryScope } from "./LibraryToolbar";

export const PROMOTE_CONFIRM = {
  title: "Make these global?",
  description: "Every coach will see them. The original coach keeps them too.",
  confirmLabel: "Make global",
  cancelLabel: "Cancel",
};

interface SelectionBarProps {
  mode: LibraryMode;
  selected: AssetSelection;
  /** Admin mode: Make global is offered in Coach scope. */
  scope?: LibraryScope;
  /** After a run: what's left selected (the ones that failed, so they can be retried). */
  onSelectedChange(next: AssetSelection): void;
}

type BulkAction = "publish" | "archive" | "restore" | "promote";

const APPLIES: Record<BulkAction, (asset: BackgroundAsset) => boolean> = {
  publish: asset => asset.visibility === "draft",
  archive: asset => asset.visibility !== "archived",
  restore: asset => asset.visibility === "archived",
  promote: asset => asset.coachId !== null,
};

const RUN: Record<BulkAction, (asset: BackgroundAsset) => Promise<BackgroundAsset>> = {
  publish: asset => BackgroundAssetServices.update(asset.id, { visibility: "active" }),
  archive: asset => BackgroundAssetServices.update(asset.id, { visibility: "archived" }),
  restore: asset => BackgroundAssetServices.update(asset.id, { visibility: "active" }),
  promote: asset => BackgroundAssetServices.promote(asset.id),
};

/** Bulk curation over the selected tiles: one request per asset, with a running count. */
export const SelectionBar: React.FC<SelectionBarProps> = ({ mode, selected, scope, onSelectedChange }) => {
  const queryClient = useQueryClient();
  const [running, setRunning] = React.useState(false);
  const assets = Array.from(selected.values());

  if (assets.length === 0) return null;

  const run = async (action: BulkAction) => {
    const rows = assets.filter(APPLIES[action]);
    if (rows.length === 0) return;
    if (action === "promote" && !(await confirmDialog(PROMOTE_CONFIRM))) return;

    setRunning(true);
    const toastId = toast.loading(`0 of ${rows.length}…`);
    let done = 0;
    const failed: AssetSelection = new Map();
    for (const asset of rows) {
      try {
        const saved = await RUN[action](asset);
        queryClient.setQueryData(designKeys.backgrounds.asset(asset.id), saved);
        done += 1;
      } catch {
        failed.set(asset.id, asset);
      }
      toast.loading(`${done + failed.size} of ${rows.length}…`, { id: toastId });
    }
    const failedMessage = `${failed.size} couldn't be updated. Try again.`;
    if (done > 0) {
      toast.success(`Updated ${done} backgrounds`, { id: toastId });
      if (failed.size > 0) toast.error(failedMessage);
    } else {
      // Nothing worked: the progress toast becomes the error rather than a success.
      toast.error(failedMessage, { id: toastId });
    }
    queryClient.invalidateQueries({ queryKey: designKeys.backgrounds.all });
    setRunning(false);
    onSelectedChange(failed);
  };

  const count = (action: BulkAction) => assets.filter(APPLIES[action]).length;
  const isAdmin = mode === "admin";

  return (
    <div
      className="sticky bottom-0 z-10 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-lg"
      data-testid="selection-bar"
    >
      <span className="text-sm font-semibold text-slate-700">{assets.length} selected</span>
      <div className="ml-auto flex flex-wrap items-center gap-2">
        {isAdmin && (
          <Button size="sm" onClick={() => void run("publish")} disabled={running || count("publish") === 0}>
            Publish
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={() => void run("archive")}
          disabled={running || count("archive") === 0}
        >
          Archive
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => void run("restore")}
          disabled={running || count("restore") === 0}
        >
          Restore
        </Button>
        {isAdmin && scope === "coach" && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => void run("promote")}
            disabled={running || count("promote") === 0}
          >
            Make global
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => onSelectedChange(new Map())} disabled={running}>
          Clear
        </Button>
      </div>
    </div>
  );
};
